import { inject, Injectable, Signal, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

export type PopoverOptionStyle = 'danger' | 'default';
export type PopoverPlacement = 'bottom-end' | 'bottom-start' | 'top-end' | 'top-start';

export interface PopoverAction<T> {
  disabled?: boolean;
  icon?: string;
  label: string;
  onClick: (data: T) => void;
  styleClass?: PopoverOptionStyle;
}

export interface PopoverOptions<T> {
  actions: PopoverAction<T>[][];
  anchor: HTMLElement;
  data: T;
  placement: PopoverPlacement;
}

type PopoverOpenOptions<T> = Omit<PopoverOptions<T>, 'data' | 'placement'> & {
  data?: T;
  placement?: PopoverPlacement;
};

@Injectable({
  providedIn: 'root',
})
export class PopoverService<T> {
  private currentPopover = signal<PopoverOptions<T> | null>(null);
  private router = inject(Router);

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => this.close(false));
  }

  close(restoreFocus = true): void {
    const popover = this.currentPopover();
    if (!popover) {
      return;
    }
    this.currentPopover.set(null);
    if (restoreFocus && popover.anchor.isConnected) {
      requestAnimationFrame(() => popover.anchor.focus());
    }
  }

  get(): Signal<PopoverOptions<T> | null> {
    return this.currentPopover.asReadonly();
  }

  open(options: PopoverOpenOptions<T>): void {
    this.currentPopover.set({
      actions: options.actions,
      anchor: options.anchor,
      data: options.data ?? {} as T,
      placement: options.placement ?? 'bottom-end',
    });
  }
}
